// ** React Imports
import { useEffect, useState } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Chip,
  FormLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import InputAdornment from "@mui/material/InputAdornment";

// ** Logic Imports
import { useServicePermission } from "../permissions/ServicePermissions";
import { useIndustryLogic } from "screens/Settings/logic/industry-logic";
import { useSettingPermission } from "screens/Settings/permissions/SettingPermissions";
import { useDefaultResourceLogic } from "../logic/default-resource-logic";
import { useIndexLogic } from "screens/Settings/logic/index-logic";

// ** Dialog Imports
import AddIndustryDialog from "screens/Settings/component/AddIndustry";

// ** Third-Party Imports
import { toast, ToastContainer } from "react-toastify";

// ** Other Imports
import "assets/CSS/Switch.css";

// ** Unit Dialog Import
import UnitDialog from "screens/Inventory/component/UnitofMeasurement";

const ServiceConfiguration = ({
  open,
  handleClose,
  serviceContent,
  ServiceProps,
}) => {
  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    materials: false,
    labor: false,
    assets: false,
    otherCosts: false,
    subcontractor: false
  });

  // State for Unit Dialog
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [unitCallback, setUnitCallback] = useState(null);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);


  //#region Permissions
  const { ServicePermissionProps } = useServicePermission();
  const { hasServiceSavePermission } = ServicePermissionProps;
  //#endregion Permissions

  const { SettingPermissionProps } = useSettingPermission();
  const {
    hasIndustrySavePermission,
  } = SettingPermissionProps;

  const { IndustryProps } = useIndustryLogic(toast);
  const {
    handleOpenIndustryDlg,
    fetchIndustries,
    openIndustryDlg,
    handleCloseIndustryDlg,
    rows,
  } = IndustryProps;

  const {
    handleSaveService,
    handleServiceChange,
    service,
    serviceErrors,
    ResetService,
  } = ServiceProps;
  const { SkillProps } = useIndexLogic(toast);
  const { skillsList, handleOpenSkillTypeDlg, openSkillTypeDlg, handleCloseSkillTypeDlg } = SkillProps;

  const { DefaultResourceProps } = useDefaultResourceLogic();
  const {
    allMaterials,
    materialTypes,
    allAssets,
    assetTypes,
    skillTypes,
    allEmployees,
    subcontractors,
    fetchDefaultResources,
    defaultAssets,
    setDefaultAssets,
    defaultMaterials,
    setDefaultMaterials,
    defaultLabors,
    setDefaultLabors,
    defaultSubcontractors,
    setDefaultSubcontractors,
    defaultMiscCosts,
    setDefaultMiscCosts,
    resetDefaultResources,
    handleDeleteDefaultMaterial,
    handleDeleteDefaultAsset,
    handleDeleteDefaultLabor,
    handleDeleteDefaultSubcontractor,
    handleDeleteDefaultMiscCost,
    units,
    fetchUnitsData, 
  } = DefaultResourceProps;

  const theme = useTheme();

  useEffect(() => {
    if (open) {
      fetchIndustries();
      // Fetch units when dialog opens
      if (fetchUnitsData) {
        fetchUnitsData();
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open || hasAutoExpanded) return;

    setExpandedSections({
      materials: defaultMaterials?.some(
        m => m.materialID || m.materialTypeID
      ),
      labor: defaultLabors?.some(
        l => l.employeeID || l.skillID
      ),
      assets: defaultAssets?.some(
        a => a.assetID || a.assetTypeID
      ),
      subcontractor: defaultSubcontractors?.some(
        s => s.employeeID || s.skillID
      ),
      otherCosts: defaultMiscCosts?.some(
        c => c.costName || c.costAmount > 0
      ),
    });

    setHasAutoExpanded(true);
  }, [
    open,
    hasAutoExpanded,
    defaultMaterials,
    defaultLabors,
    defaultAssets,
    defaultSubcontractors,
    defaultMiscCosts,
  ]);



  useEffect(() => {
    if (service?.serviceOfferingID) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  }, [service?.serviceOfferingID]);

  const handleCloseControl = () => {
    setHasAutoExpanded(false);  
    ResetService();
    resetDefaultResources();
    handleClose();
  };


  // Function to reset both service details and resource details
  const handleResetAll = () => {
    ResetService(); // Reset service details
    resetDefaultResources(); // Reset resource details
    
    // Reset to initial state for all resources
    setDefaultMaterials([{
      id: 1,
      serviceOfferingID: null,
      materialID: null,
      materialTypeID: null,
      quantity: 0,
      unitID: null,
      costPerUnit: 0,
      pricePerUnit: 0,
      markup: 0
    }]);
    
    setDefaultLabors([{
      id: 1,
      serviceOfferingID: null,
      employeeID: null,
      skillID: null,
      manHours: 0,
      costPerHour: 0,
      markup: 0,
    }]);

    setDefaultSubcontractors([{
      id: 1,
      serviceOfferingID: null,
      employeeID: null,
      skillID: null,
      unitID: 0,
      unitQuantity: 0,
      costPerUnit: 0,
      pricePerUnit: 0,
      markup: 0,
    }]);
    
    setDefaultAssets([{
      id: 1,
      serviceOfferingID: null,
      assetTypeID: null,
      assetID: null,
      quantity: 0,
      usageDuration: 0,
      costPerUnit: 0,
      pricePerUnit: 0,
      markup: 0
    }]);
    
    setDefaultMiscCosts([{
      id: 1,
      serviceOfferingID: null,
      costName: "",
      costAmount: 0,
      customerPrice: 0,
      billToCustomer: false,
    }]);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Utility function to remove leading zeros
  const removeLeadingZeros = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    
    const str = value.toString().trim();
    
    // Handle decimal numbers
    if (str.includes('.')) {
      const parts = str.split('.');
      // Remove leading zeros from integer part, but keep at least one zero if it's just "0."
      parts[0] = parts[0].replace(/^0+/, '') || '0';
      return parts.join('.');
    }
    
    // Handle integers - remove leading zeros but keep at least one zero if the value is just "0"
    return str.replace(/^0+/, '') || '0';
  };

  // Handle opening unit dialog
  const handleOpenUnitDialog = (callback) => {
    setUnitCallback(() => callback);
    setUnitDialogOpen(true);
  };

  // Handle unit creation callback
  const handleUnitCreatedCallback = (result) => {
    if (unitCallback) {
      unitCallback(result);
    }
    setUnitDialogOpen(false);
    setUnitCallback(null);
    // Refresh units list
    if (fetchUnitsData) {
      fetchUnitsData();
    }
  };

  // Prepare unit options including "Add New" option
  const unitOptions = [
    { value: '__ADD_NEW_UNIT__', label: '+ Create New Unit', isAddNew: true },
    ...(units || []).map(unit => ({
      value: unit.value,
      label: unit.label
    }))
  ];

  // Handle unit selection with "Add New" option for Material
  const handleMaterialUnitChange = (materialId, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitName) {
          updateMaterial(materialId, "unitID", result.unitID);
        }
      });
    } else {
      // Set the selected unit
      updateMaterial(materialId, "unitID", newValue?.value || "");
    }
  };

  const handleSubcontractorUnitChange = (subcontractorId, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitName) {
          updateSubcontractor(subcontractorId, "unitID", result.unitID);
        }
      });
    } else {
      // Set the selected unit
      updateSubcontractor(subcontractorId, "unitID", newValue?.value || "");
    }
  };

  // Material functions
  const addMaterial = () => {
    const highestId = defaultMaterials.reduce((max, material) => {
      const currentId = material.defaultJobMaterialID || material.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultMaterials([
      ...defaultMaterials,
      {
        id: newId,
        serviceOfferingID: null,
        materialID: null,
        materialTypeID: null,
        quantity: 0,
        unitID: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
        markup: 0
      },
    ]);
  };

 const removeMaterial = (id) => {
  const materialToDelete = defaultMaterials.find(m => m.id === id);

  if (materialToDelete?.defaultJobMaterialID) {
    const response = handleDeleteDefaultMaterial(id);
    if (response) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  } else {
    setDefaultMaterials(prev =>
      prev.filter(material => material.id !== id)
    );
  }
};


  const updateMaterial = (id, field, value) => {
    if(field === 'materialID'){
      const selectedMaterial = allMaterials?.find((m) => m.materialID === value);
      console.log('selectedMaterial', selectedMaterial)
      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { 
            ...material,
            [field]: value,
            unitID: selectedMaterial?.unitID > 0 ? selectedMaterial.unitID : 0,
            costPerUnit: selectedMaterial?.costPerUnit,
            pricePerUnit: selectedMaterial?.perUnitPrice,
          } : material
        )
      );
    } else if(field === 'pricePerUnit'){
        setDefaultMaterials(
          defaultMaterials?.map((material) =>
            material.id === id ? { 
              ...material, 
              [field]: value,
              markup: material.costPerUnit && value
                  ? Math.round(((parseFloat(value) - parseFloat(material.costPerUnit)) / parseFloat(material.costPerUnit)) * 100)
                  : 0
            } : material
          )
        );
    } else if(field === 'markup'){
      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { 
            ...material, 
            [field]: value,
            pricePerUnit: material.costPerUnit && value
                ? parseFloat(material.costPerUnit) * (1 + parseFloat(value) / 100)
                : 0
          } : material
        )
      );
    } else if(field === 'costPerUnit'){
      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { 
            ...material, 
            [field]: value,
            pricePerUnit: value && material.markup
                ? parseFloat(value) * (1 + parseFloat(material.markup) / 100)
                : material.pricePerUnit
          } : material
        )
      );
    } else{
      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { ...material, [field]: value } : material
        )
      );
    }
  };

  // Labor functions
  const addLabor = () => {
    const highestId = defaultLabors.reduce((max, labor) => {
      const currentId = labor.defaultJobLaborID || labor.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultLabors([
      ...defaultLabors,
      {
        id: newId,
        serviceOfferingID: null,
        employeeID: null,
        skillID: null,
        manHours: 0,
        costPerHour: 0,
        markup: 0,
      },
    ]);
  };

 const removeLabor = (id) => {
  const laborToDelete = defaultLabors.find(l => l.id === id);

  if (laborToDelete?.defaultJobLaborID && laborToDelete.defaultJobLaborID > 0) {
    const response = handleDeleteDefaultLabor(id);
    if (response) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  } else {
    setDefaultLabors(prev =>
      prev.filter(labor => labor.id !== id)
    );
  }
};


const updateLabor = (id, field, value) => {
  if (field === 'employeeID') {
    const selectedEmployee = allEmployees?.find((e) => e.employeeID === value);

    setDefaultLabors(
      defaultLabors?.map((labor) => {
        if (labor.id === id) {
          // If employee is cleared (value = 0)
          if (value === 0) {
            return {
              ...labor,
              [field]: value,
              manHours: 0,
              costPerHour: 0,
              markup: 0,
            };
          }
          if (selectedEmployee) {
            return {
              ...labor,
              [field]: value,
              skillID: selectedEmployee.skillID || labor.skillID, 
              costPerHour: selectedEmployee?.wageType !== "Salary" ? selectedEmployee?.wageAmount : 0,
            };
          }
          
          return { ...labor, [field]: value };
        }
        return labor;
      })
    );
  } else if (field === 'skillID') {
    setDefaultLabors(
      defaultLabors?.map((labor) => {
        if (labor.id === id) {
          if (value === 0) {
            return {
              ...labor,
              [field]: value,
            };
          }
          if (labor.employeeID && labor.employeeID !== 0) {
            const currentEmployee = allEmployees?.find(e => e.employeeID === labor.employeeID);
            if (currentEmployee && currentEmployee.skillID !== value) {
              return {
                ...labor,
                [field]: value,
                employeeID: 0, 
                manHours: 0,
                costPerHour: 0,
                markup: 0,
              };
            }
          }
          
          return { ...labor, [field]: value };
        }
        return labor;
      })
    );
  } else {
    setDefaultLabors(
      defaultLabors?.map((labor) =>
        labor.id === id ? { ...labor, [field]: value } : labor
      )
    );
  }
};

  // Subcontractor functions
  const addSubcontractor = () => {
    const highestId = defaultSubcontractors.reduce((max, subcontractor) => {
      const currentId = subcontractor.defaultJobSubcontractorID || subcontractor.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultSubcontractors([
      ...defaultSubcontractors,
      {
        id: newId,
        serviceOfferingID: null,
        employeeID: null,
        skillID: null,
        unitID: null,
        unitQuantity: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
        markup: 0,
      },
    ]);
  };

const removeSubcontractor = (id) => {
  const subcontractorToDelete = defaultSubcontractors.find(s => s.id === id);

  if (
    subcontractorToDelete?.defaultJobSubcontractorID &&
    subcontractorToDelete.defaultJobSubcontractorID > 0
  ) {
    const response = handleDeleteDefaultSubcontractor(id);
    if (response) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  } else {
    setDefaultSubcontractors(prev =>
      prev.filter(sub => sub.id !== id)
    );
  }
};


  const updateSubcontractor = (id, field, value) => {
      if(field === 'pricePerUnit'){
        setDefaultSubcontractors(
          defaultSubcontractors?.map((subcontractor) =>
            subcontractor.id === id ? { 
              ...subcontractor, 
              [field]: value,
              markup: subcontractor.costPerUnit && value
                  ? Math.round(((parseFloat(value) - parseFloat(subcontractor.costPerUnit)) / parseFloat(subcontractor.costPerUnit)) * 100)
                  : 0
            } : subcontractor
          )
        );
      } else if(field === 'markup'){
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { 
            ...subcontractor, 
            [field]: value,
            pricePerUnit: subcontractor.costPerUnit && value
                ? parseFloat(subcontractor.costPerUnit) * (1 + parseFloat(value) / 100)
                : 0
          } : subcontractor
        )
      );
    } else if(field === 'costPerUnit'){
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { 
            ...subcontractor, 
            [field]: value,
            pricePerUnit: value && subcontractor.markup
                ? parseFloat(value) * (1 + parseFloat(subcontractor.markup) / 100)
                : subcontractor.pricePerUnit
          } : subcontractor
        )
      );
    } else{
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { ...subcontractor, [field]: value } : subcontractor
        )
      );
    }
  };

  const handleSave = async ( defaultAssets, 
    defaultMaterials, defaultLabors, defaultSubcontractors,
    defaultMiscCosts)  => {
    handleSaveService( 
      defaultAssets,
      defaultMaterials,
      defaultLabors,
      defaultSubcontractors,
      defaultMiscCosts
    );
    await new Promise(resolve => setTimeout(resolve, 100));  
  }
  
  // Asset functions
  const addAsset = () => {
    const highestId = defaultAssets.reduce((max, asset) => {
      const currentId = asset.defaultJobAssetID || asset.id;
      return currentId > max ? currentId : max;
    }, 0);

    const newId = highestId + 1;

    setDefaultAssets([
      ...defaultAssets,
      {
        id: newId,
        serviceOfferingID: null,
        assetTypeID: null,
        assetID: null,
        quantity: 0,
        usageDuration: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
        markup: 0
      },
    ]);
  };

 const removeAsset = (id) => {
  const assetToDelete = defaultAssets.find(a => a.id === id);

  if (assetToDelete?.defaultJobAssetID && assetToDelete.defaultJobAssetID > 0) {
    const response = handleDeleteDefaultAsset(id);
    if (response) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  } else {
    setDefaultAssets(prev =>
      prev.filter(asset => asset.id !== id)
    );
  }
};


  const updateAsset = (id, field, value) => {
    if(field === 'assetID'){
      const selectedAsset = allAssets.find((a) => a.assetID === value);

      setDefaultAssets(
        defaultAssets?.map((asset) =>
          asset.id === id ? { 
            ...asset,
            [field]: value,
            costPerUnit: selectedAsset?.operationalCostsAmount || 0
          } : asset
        )
      );
    } else if(field === 'pricePerUnit'){
        setDefaultAssets(
          defaultAssets?.map((asset) =>
            asset.id === id ? { 
              ...asset, 
              [field]: value,
              markup: asset.costPerUnit && value
                  ? Math.round(((parseFloat(value) - parseFloat(asset.costPerUnit)) / parseFloat(asset.costPerUnit)) * 100)
                  : 0
            } : asset
          )
        );
      } else if(field === 'markup'){
        setDefaultAssets(
          defaultAssets?.map((asset) =>
            asset.id === id ? { 
              ...asset, 
              [field]: value,
              pricePerUnit: asset.costPerUnit && value
                  ? parseFloat(asset.costPerUnit) * (1 + parseFloat(value) / 100)
                  : 0
            } : asset
          )
        );
      } else if(field === 'costPerUnit'){
        setDefaultAssets(
          defaultAssets?.map((asset) =>
            asset.id === id ? { 
              ...asset, 
              [field]: value,
              pricePerUnit: value && asset.markup
                  ? parseFloat(value) * (1 + parseFloat(asset.markup) / 100)
                  : asset.pricePerUnit
            } : asset
          )
        );
      } else{
      setDefaultAssets(
        defaultAssets?.map((asset) =>
          asset.id === id ? { ...asset, [field]: value } : asset
        )
      );
    }
  };

  // Other Costs functions
  const addOtherCost = () => {
    const highestId = defaultMiscCosts.reduce((max, miscCost) => {
      const currentId = miscCost.defaultJobMiscCostID || miscCost.id;
      return currentId > max ? currentId : max;
    }, 0);

    const newId = highestId + 1;

    setDefaultMiscCosts([
      ...defaultMiscCosts,
      {
        id: newId,
        serviceOfferingID: null,
        costName: "",
        costAmount: 0,
        customerPrice: 0,
        billToCustomer: false,
      },
    ]);
  };

  const removeOtherCost = (id) => {
    if (defaultMiscCosts.length > 1) {
      const miscCostToDelete = defaultMiscCosts.find((dm) => dm.id === id);

      if(miscCostToDelete.defaultJobMiscCostID && miscCostToDelete.defaultJobMiscCostID > 0){
        const response = handleDeleteDefaultMiscCost(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultMiscCosts(defaultMiscCosts.filter((cost) => cost.id !== id));
      }

    }
  };

  const updateOtherCost = (id, field, value) => {
    setDefaultMiscCosts(
      defaultMiscCosts?.map((cost) =>
        cost.id === id ? { ...cost, [field]: value } : cost
      )
    );
  };

  const augmentedIndustryOptions = [
    { industryID: -1, industryName: "+ Add Industry" },
    ...rows?.map((row) => ({
      industryID: row.industryID,
      industryName: row.industryName,
    })),
  ];

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined) return "";

    const str = value.toString();

    // Allow partial numbers while typing (e.g. ".", "0.", ".5")
    if (str === "" || str === "." || str === "0." || /^\.\d*$/.test(str)) {
      return str;
    }

    // Remove commas and format integer part
    const parts = str.replace(/,/g, "").split(".");
    if (parts[0] !== "") {
      const num = Number(parts[0]);
      // Check if it's actually a valid number before formatting
      if (!isNaN(num)) {
        parts[0] = num.toLocaleString("en-US");
      }
    }

    return parts.join(".");
  };

  const removeCommas = (value) => value.replace(/,/g, "");

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        onClose={handleCloseControl}
        aria-labelledby="form-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            width: "90%",
            maxWidth: "1200px",
            height: "90vh",
            maxHeight: "730px",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Service Configuration
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ height: "calc(100% - 180px)", overflow: "auto" }}
        >
          {/* Service Details Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
              Service Details
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <CustomTextField
                  fullWidth
                  required
                  label="Service Name"
                  placeholder="Enter service name"
                  value={service?.serviceOfferingName || ""}
                  onChange={(e) => {
                    handleServiceChange(
                      "serviceOfferingName",
                      e.target.value
                    );
                  }}
                  error={!!serviceErrors.serviceOfferingName}
                  helperText={serviceErrors.serviceOfferingName}
                  sx={{ maxWidth: "1000px" }}
                />
              </Grid>
               <Grid item xs={12} md={4}>
              <Box >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                <Tooltip
                  arrow
                  title={
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Toggle between Active and Inactive states for this
                      service. Active means it's currently available for
                      use, while Inactive disables it until reactivated.
                    </Typography>
                  }
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        transform: "translateX(10px) !important",
                      },
                    },
                  }}
                >
                  <IconButton size="small" color="info">
                    <Icon icon="tabler:info-circle" style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <FormLabel>
                  {service.isActive ? "Active" : "Inactive"}
                </FormLabel>
              </Box>
              <Box sx={{ mt: 0.3,ml:6}}>
                <label className="switch wide-switch">
                  <input
                    type="checkbox"
                    checked={service.isActive ?? false}
                    onChange={(e) => {
                      handleServiceChange("isActive", e.target.checked);
                    }}
                  />
                  <span className="slider round"></span>
                </label>
              </Box>
            </Box>
              </Grid>
             
            <Grid item xs={12} md={8}>
              <CustomAutocomplete
                fullWidth
                required
                value={
                  augmentedIndustryOptions.find(
                    (industry) =>
                      industry.industryID === service?.industryID
                  ) || null
                }
                onChange={(e, newValue) => {
                  if (newValue && newValue.industryID === -1) {
                    handleOpenIndustryDlg();
                    return;
                  }
                  handleServiceChange(
                    "industryID",
                    newValue ? newValue.industryID : null
                  );
                }}
                options={augmentedIndustryOptions}
                getOptionLabel={(option) => option?.industryName || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.industryID === value?.industryID
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Select Industry"
                    label="Industry"
                    required
                    error={!!serviceErrors.industryID}
                    helperText={serviceErrors.industryID}
                    sx={{ maxWidth: "1000px" }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.industryID}>
                    <span style={{
                      fontWeight: option.industryID === -1 ? 'bold' : 'normal',
                      display: 'block',
                    }}>
                      {option.industryName}
                    </span>
                  </li>
                )}
                renderTags={(value, getTagProps) => {
                  if (!value) return null;
                  return (
                    <Chip
                      label={
                        <Typography variant="body2">
                          {value.industryName}
                        </Typography>
                      }
                      {...getTagProps({ index: 0 })}
                      key={value.industryID}
                    />
                  );
                }}
                sx={{ maxWidth: "1000px" }}
              />
            </Grid>
              <Grid item xs={12} md={8}>
                <CustomTextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  placeholder="Add a description..."
                  value={service?.description || ""}
                  onChange={(e) => {
                    handleServiceChange("description", e.target.value);
                  }}
                  sx={{ maxWidth: "1000px" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label={ 
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                         <Tooltip 
                  title={
                  <Box>
                   <Typography variant="body2" sx={{ color: "white" }}>
                    This amount will only be used by default if you charge a Flat Rate for this Service. It can always be changed at the time of Estimate or Work Order. If you charge for Time and Materials, this Flat Rate will not be used.
                    </Typography>
                    </Box>
                  }
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          transform: 'translateX(10px) !important', 
                        }
                      }
                    }}
                  >
                  <IconButton size="small" sx={{ p: 0, color: "text.secondary" }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                Flat Rate
                  </Box>
                  }
                  placeholder="Enter service's flat rate"
                  value={formatNumberWithCommas(service?.flatRate || "")}
                  onChange={(e) => {
                    const rawValue = removeCommas(e.target.value);
                    const cleanedValue = removeLeadingZeros(rawValue);
                    handleServiceChange("flatRate", cleanedValue);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <span style={{ fontSize: "1.3rem" }}>$</span>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: "600px" }}
                />
              </Grid>
              
            </Grid>
          </Box>

          {/* Resource Details Section */}
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
               Default Resources
                <Tooltip 
                  title={
                  <Box>
                   <Typography variant="body2" sx={{ color: "white" }}>
                   These resources will populate by default whenever this Service is added to an Estimate or Work Order. You can always change them at the Estimate or Work Order level.
                    </Typography>
                    </Box>
                  }
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          transform: 'translateX(10px) !important', 
                        }
                      }
                    }}
                  >
                  <IconButton size="small" sx={{ p: 0, ml: 1, color: "text.secondary" }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
            </Typography>

            {/* Materials Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('materials')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Materials
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.materials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.materials}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%" ,textTransform: 'none'  }}>
                              Material Type <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "15%" , textTransform: 'none'  }}>
                              Material Name<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              UoM <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Cost Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "13%", textTransform: 'none' }}>
                              Price Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultMaterials?.map((material) => (
                            <TableRow key={material.id}>
                             <TableCell>
                            <CustomAutocomplete
                              fullWidth
                              size="small"
                              options={(materialTypes || []).sort((a, b) => 
                                (a.typeName || '').localeCompare(b.typeName || '')
                              )}
                              getOptionLabel={(option) => option?.typeName || ''}
                              value={materialTypes?.find(item => item.typeCatalogID === material.materialTypeID) || null}
                              onChange={(event, newValue) => {
                                updateMaterial(
                                  material.id,
                                  "materialTypeID",
                                  newValue?.typeCatalogID || 0
                                );
                              }}
                              isOptionEqualToValue={(option, value) => option?.typeCatalogID === value?.typeCatalogID}
                              renderInput={(params) => (
                                <CustomTextField
                                  {...params}
                                  size="small"
                                  placeholder="Select type"
                                />
                              )}
                            />
                    </TableCell>
                    <TableCell>
                      <CustomAutocomplete
                        fullWidth
                        size="small"
                        disabled={!material.materialTypeID}
                        options={
                          material.materialTypeID
                            ? allMaterials?.filter(m => m.materialTypeID === material.materialTypeID) || []
                            : allMaterials || []
                        }
                        getOptionLabel={(option) => option?.itemName || ''}
                        value={allMaterials?.find(item => item.materialID === material.materialID) || null}
                        onChange={(event, newValue) => {
                          updateMaterial(
                            material.id,
                            "materialID",
                            newValue?.materialID || 0
                          );
                        }}
                        isOptionEqualToValue={(option, value) => option?.materialID === value?.materialID}
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter(option =>
                            option?.itemName?.toLowerCase().includes(searchTerm)
                          );
                        }}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            size="small"
                            placeholder="Select material"
                            disabled={!material.materialTypeID}
                          />
                        )}
                      />
                    </TableCell>
                              <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  required
                                  options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
                                  value={
                                    unitOptions.find(option => option.value === material?.unitID) || null
                                  }
                                  onChange={(event, newValue) => handleMaterialUnitChange(material.id, newValue)}
                                  getOptionLabel={(option) => option.label}
                                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                                  filterOptions={(options, { inputValue }) => {
                                    const searchTerm = inputValue.toLowerCase();
                                    return options.filter(option => 
                                      option.label.toLowerCase().includes(searchTerm)
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select Unit"
                                      InputLabelProps={{
                                        sx: {
                                          mb: 2, 
                                        }
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: option.isAddNew ? 'bold' : 'normal',
                                        }}
                                      >
                                        {option.label}
                                      </Typography>
                                    </li>
                                  )}
                                  PaperProps={{
                                    sx: {
                                      maxHeight: 200
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={material.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={material.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={material.quantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "quantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={material.markup}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "markup",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    removeMaterial(material.id)
                                  }
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultMaterials?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No materials added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addMaterial}
                          >
                            Add Material
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultMaterials?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addMaterial}
                        >
                          Add Material
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Labor Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('labor')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Labor
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.labor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.labor}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Skill Type 
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Employee Name 
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Man Hours <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "14%", textTransform: 'none' }}>
                              Cost per Hour <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none'  }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                         {defaultLabors?.map((labor) => (
                          <TableRow key={labor.id}>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                            <CustomAutocomplete
                          fullWidth
                          size="small"
                          options={(skillTypes || []).sort((a, b) => 
                            (a.skillName || '').localeCompare(b.skillName || '')
                          )}
                          getOptionLabel={(option) => option?.skillName || ''}
                          value={skillTypes?.find(item => item.skillID === labor?.skillID) || null}
                          onChange={(event, newValue) => {
                            updateLabor(labor.id, "skillID", newValue?.skillID || 0);
                          }}
                          isOptionEqualToValue={(option, value) => option?.skillID === value?.skillID}
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              size="small"
                              placeholder="Select skill type"
                            />
                          )}
                        />
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                          <CustomAutocomplete
                          key={`employee-${labor.id}-${labor.employeeID}`} // Add this line
                          fullWidth
                          size="small"
                          clearOnBlur={false}
                          clearOnEscape
                          disableClearable={false}
                          options={
                            ((!labor?.skillID || labor?.skillID === 0)
                              ? (allEmployees?.filter(x => x.empType !== 'Sub Contractors') || [])
                              : (allEmployees?.filter(x => x.skillID === labor?.skillID && x.empType !== 'Sub Contractors') || [])
                            )
                            .sort((a, b) =>
                              `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)
                            )
                          }
                          getOptionLabel={(option) => {
                            if (!option) return '';
                            if (option.employeeID === 0) return "";
                            return `${option.firstName || ''} ${option.lastName || ''}`.trim();
                          }}
                          value={
                            allEmployees?.find(item => item.employeeID === labor?.employeeID && item.empType !== 'Sub Contractors') ||
                            null
                          }
                          onChange={(event, newValue) => {
                            updateLabor(labor.id, "employeeID", newValue?.employeeID || 0);
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option?.employeeID === value?.employeeID
                          }
                          filterOptions={(options, { inputValue }) => {
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option =>
                              `${option.firstName || ''} ${option.lastName || ''}`.toLowerCase().includes(searchTerm)
                            );
                          }}
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              size="small"
                              placeholder="Select employee"
                            />
                          )}
                        />
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={labor.manHours}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateLabor(labor.id, "manHours", rawValue);
                                }}
                                size="small"
                              disabled={!(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0)}
                                InputProps={{
                                  readOnly: !(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0),
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ width: "14%", textTransform: 'none' }}>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0.00"
                                value={labor.costPerHour}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateLabor(labor.id, "costPerHour", rawValue);
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <span>$</span>
                                    </InputAdornment>
                                  ),
                                  readOnly: !(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0),
                                }}
                                size="small"
                              disabled={!(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0)}
                              />
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={labor.markup}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateLabor(labor.id, "markup", rawValue);
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value && value.trim() !== '') {
                                    const numericValue = parseFloat(value);
                                    if (!isNaN(numericValue)) {
                                      const roundedValue = Math.round(numericValue);
                                      updateLabor(labor.id, "markup", roundedValue.toString());
                                    }
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <span>%</span>
                                    </InputAdornment>
                                  ),
                                  readOnly: !(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0),
                                }}
                                size="small"
                              disabled={!(labor.skillID && labor.skillID !== 0) && !(labor.employeeID && labor.employeeID !== 0)}
                              />
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none' }}>
                              <IconButton
                                color="error"
                                onClick={() => removeLabor(labor.id)}
                                size="small"
                              >
                                <Icon icon="tabler:trash" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        </TableBody>
                      </Table>
                      {defaultLabors?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No labor added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addLabor}
                          >
                            Add Labor
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultLabors?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addLabor}
                        >
                          Add Labor
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Assets Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('assets')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Assets
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.assets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.assets}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "15%", textTransform: 'none'  }}>
                              Asset Type<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "15%" , textTransform: 'none' }}>
                              Asset Name <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%" , textTransform: 'none' }}>
                              Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%" , textTransform: 'none' }}>
                              Usage Duration <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none'  }}>
                              Cost per Asset <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "14%" , textTransform: 'none' }}>
                              Price per Asset <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "14%" , textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultAssets?.map((asset) => (
                            <TableRow key={asset.id}>
                                <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  size="small"
                                  options={assetTypes || []}
                                  getOptionLabel={(option) => option?.typeName || ''}
                                  value={assetTypes?.find(item => item.typeCatalogID === asset.assetTypeID) || null}
                                  onChange={(event, newValue) => {
                                    updateAsset(
                                      asset.id,
                                      "assetTypeID",
                                      newValue?.typeCatalogID || 0
                                    );
                                  }}
                                  isOptionEqualToValue={(option, value) => option?.typeCatalogID === value?.typeCatalogID}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select type"
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  size="small"
                                  disabled={!asset.assetTypeID}
                                  options={
                                    asset.assetTypeID
                                      ? allAssets?.filter(a => a.assetTypeID === asset.assetTypeID) || []
                                      : allAssets || []
                                  }
                                  getOptionLabel={(option) => option?.assetName || ''}
                                  value={allAssets?.find(item => item.assetID === asset.assetID) || null}
                                  onChange={(event, newValue) => {
                                    updateAsset(
                                      asset.id,
                                      "assetID",
                                      newValue?.assetID || 0
                                    );
                                  }}
                                  isOptionEqualToValue={(option, value) => option?.assetID === value?.assetID}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select asset"
                                      disabled={!asset.assetTypeID}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={asset.quantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "quantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  placeholder="e.g., 8 hours"
                                  value={asset.usageDuration}
                                  onChange={(e) =>
                                    updateAsset(
                                      asset.id,
                                      "usageDuration",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={asset.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={asset.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={asset.markup}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "markup",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeAsset(asset.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultAssets?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No assets added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addAsset}
                          >
                            Add Asset
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultAssets?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addAsset}
                        >
                          Add Asset
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Other Costs Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('otherCosts')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Other Costs
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.otherCosts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.otherCosts}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "25%" , textTransform: 'none' }}>
                              Cost Name<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%" , textTransform: 'none' }}>
                              Cost Amount <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none'  }}>
                              Customer Price <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "20%" , textTransform: 'none' }}>
                              Bill to Customer? <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%" , textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultMiscCosts?.map((cost) => (
                            <TableRow key={cost.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  placeholder="Cost name"
                                  value={cost.costName}
                                  onChange={(e) =>
                                    updateOtherCost(
                                      cost.id,
                                      "costName",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={cost.costAmount}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateOtherCost(
                                      cost.id,
                                      "costAmount",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={cost.customerPrice}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateOtherCost(
                                      cost.id,
                                      "customerPrice",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={cost.billToCustomer}
                                      onChange={(e) =>
                                        updateOtherCost(
                                          cost.id,
                                          "billToCustomer",
                                          e.target.checked
                                        )
                                      }
                                      color="success"
                                    />
                                  }
                                  label="Bill to Customer"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeOtherCost(cost.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultMiscCosts?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No other costs added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addOtherCost}
                          >
                            Add Other Cost
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultMiscCosts?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addOtherCost}
                        >
                          Add Other Cost
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Subcontractor Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('subcontractor')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Subcontractor
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.subcontractor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.subcontractor}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Skill Type <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Subcontractor Name <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Unit of Measure <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Unit Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Cost Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Price Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none'  }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultSubcontractors?.map((subcontractor) => (
                            <TableRow key={subcontractor.id}>
                            <TableCell>
                              <CustomAutocomplete
                                fullWidth
                                size="small"
                                options={skillTypes || []}
                                getOptionLabel={(option) => option?.skillName || ''}
                                value={skillTypes?.find(item => item.skillID === subcontractor.skillID) || null}
                                onChange={(event, newValue) => {
                                  updateSubcontractor(
                                    subcontractor.id,
                                    "skillID",
                                    newValue?.skillID || 0
                                  );
                                }}
                                isOptionEqualToValue={(option, value) => option?.skillID === value?.skillID}
                                renderInput={(params) => (
                                  <CustomTextField
                                    {...params}
                                    size="small"
                                    placeholder="Select skill"
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomAutocomplete
                                fullWidth
                                size="small"
                                disabled={!subcontractor.skillID}
                                options={
                                  subcontractor.skillID
                                    ? subcontractors?.filter(e => e.skillID === subcontractor.skillID) || []
                                    : subcontractors || []
                                }
                                getOptionLabel={(option) => {
                                  if (!option) return '';
                                  return `${option.firstName || ''} ${option.lastName || ''}`.trim();
                                }}
                                value={subcontractors?.find(item => item.employeeID === subcontractor.employeeID) || null}
                                onChange={(event, newValue) => {
                                  updateSubcontractor(
                                    subcontractor.id,
                                    "employeeID",
                                    newValue?.employeeID || 0
                                  );
                                }}
                                isOptionEqualToValue={(option, value) => option?.employeeID === value?.employeeID}
                                renderInput={(params) => (
                                  <CustomTextField
                                    {...params}
                                    size="small"
                                    placeholder="Select subcontractor"
                                    disabled={!subcontractor.skillID}
                                  />
                                )}
                              />
                            </TableCell>
                              <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  required
                                  options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
                                  value={
                                    unitOptions.find(option => option.value === subcontractor?.unitID) || null
                                  }
                                  onChange={(event, newValue) => handleSubcontractorUnitChange(subcontractor.id, newValue)}
                                  getOptionLabel={(option) => option.label}
                                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                                  filterOptions={(options, { inputValue }) => {
                                    const searchTerm = inputValue.toLowerCase();
                                    return options.filter(option => 
                                      option.label.toLowerCase().includes(searchTerm)
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select Unit"
                                      InputLabelProps={{
                                        sx: {
                                          mb: 2, 
                                        }
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: option.isAddNew ? 'bold' : 'normal',
                                        }}
                                      >
                                        {option.label}
                                      </Typography>
                                    </li>
                                  )}
                                  PaperProps={{
                                    sx: {
                                      maxHeight: 200
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={subcontractor.unitQuantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "unitQuantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={subcontractor.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={subcontractor.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                             <TableCell>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={subcontractor.markup}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateSubcontractor(
                                    subcontractor.id,
                                    "markup",
                                    rawValue
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value && value.trim() !== '') {
                                    const numericValue = parseFloat(value);
                                    if (!isNaN(numericValue)) {
                                      const roundedValue = Math.round(numericValue);
                                      updateSubcontractor(
                                        subcontractor.id,
                                        "markup",
                                        roundedValue.toString()
                                      );
                                    }
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <span>%</span>
                                    </InputAdornment>
                                  ),
                                }}
                                size="small"
                              />
                            </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeSubcontractor(subcontractor.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultSubcontractors?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No Subcontractor added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addSubcontractor}
                          >
                            Add Subcontractor
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultSubcontractors?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addSubcontractor}
                        >
                          Add Subcontractor
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            gap: 2,
          }}
          className="dialog-actions-dense"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={handleCloseControl}
            >
              Cancel
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="tonal"
              color="info"
              disabled={!hasServiceSavePermission}
              {...(hasServiceSavePermission && {
                onClick: handleResetAll,
              })}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="main"
              disabled={!hasServiceSavePermission}
              {...(hasServiceSavePermission && {
               onClick: () => {                
                handleSave(
                  defaultAssets,    
                  defaultMaterials,    
                  defaultLabors,    
                  defaultSubcontractors,    
                  defaultMiscCosts);                               
              }
              })}
            >
              {serviceContent.btnText}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <AddIndustryDialog
        open={openIndustryDlg}
        handleClose={handleCloseIndustryDlg}
        IndustryProps={IndustryProps}
      />

      {/* Unit Dialog */}
      <UnitDialog
        open={unitDialogOpen}
        handleClose={() => setUnitDialogOpen(false)}
        UnitProps={{
          existingUnit: null,
          fetchUnits: fetchUnitsData,
          unitContent: {
            title: "Add Unit of Measurement",
            entity: 'create'
          },
          onUnitCreated: handleUnitCreatedCallback
        }}
      />

      {/*<ToastContainer />*/}
    </>
  );
};

export default ServiceConfiguration;